<template>
	<StandardModal
		open
		small
		:title="$t('groupinvites_title_singular')"
		:confirmText="confirmText"
		:confirmProps="{ loading: isRedeeming }"
		:hideCancel="hasError"
		@confirm="redeem"
		@cancel="close"
		:loading="loading"
		ref="modal"
		class="invite-preview-modal"
	>
		<ProfilePic
			v-if="invite && !hasError"
			:user="invite.organization"
			size="100px"
		/>
		<i18n-t
			v-if="invite && invite.group && !hasError"
			keypath="groupinvites_signup_join_org_label"
			class="content"
			tag="div"
		>
			<template #GroupName>
				<strong>{{ invite.group.title }}</strong>
			</template>
			<template #OrgName>
				<strong>{{ invite.organization.name }}</strong>
			</template>
		</i18n-t>
		<i18n-t
			v-if="invite && !invite.group && !hasError"
			keypath="groupinvites_org_member_required"
			class="content"
			tag="div"
		>
			<template #OrgName>
				<strong v-text="invite.organization.name" />
			</template>
		</i18n-t>
		<div class="error-container" v-if="hasError">
			<CircleExclamationIcon type="regular" halo />
			<p v-if="errorText" v-text="errorText" />
		</div>
	</StandardModal>
</template>

<script>
import { StandardModal, ProfilePic } from '@/components/common'
import { fetchInviteMeta } from '@/components/Invite'
import { get, put } from '@/helpers/api'
import { getOrganization, setMembership } from '@/store'
import { CircleExclamationIcon } from '@/icons'

export default {
	components: { StandardModal, CircleExclamationIcon, ProfilePic },
	props: ['inviteCode'],
	data() {
		return {
			invite: null,
			loading: true,
			errorText: null,
			isRedeeming: false,
		}
	},

	emits: ['cancel'],

	async mounted() {
		await this.getInviteInfo()
	},

	computed: {
		hasError() {
			return !!this.errorText
		},
		confirmText() {
			if (this.hasError) {
				return this.$t('common_button_done')
			} else {
				return this.invite?.organization?.id !== getOrganization().id
					? this.$t('onboarding_splash_signin')
					: this.$t('meetings_actionsheet_button_join')
			}
		},
	},

	methods: {
		async getInviteInfo() {
			try {
				const invite = await fetchInviteMeta(this.inviteCode)
				const org = getOrganization()
				if (invite.access === 'internal' && org.id === invite.organization.id) {
					const {
						data: { data },
					} = await get(`/api/clients/invites/${this.inviteCode}`)
					invite.group = data.group
				}

				this.invite = invite
				this.loading = false

				if (invite?.group?.has_memberships_available === false) {
					this.errorText = this.$t('groupinvites_error_member_max')
				}
			} catch (e) {
				console.error(e)
				this.loading = false
				this.errorText =
					e.response?.data?.message || this.$t('apierrors_error_generic')
			}
		},

		close() {
			this.$emit('cancel')
		},

		async redeem() {
			this.modalError?.()
			if (this.hasError) {
				this.close()
				return
			}
			this.isRedeeming = true

			const org = getOrganization()
			if (this.invite.organization.id !== org.id) {
				// if invite is for another org, we need to just redirect to that invite url
				window.open(this.invite.url)
				this.close()
				return
			}

			// otherwise, we can redeem the invite
			try {
				const { data } = await put(
					`/api/clients/invites/${this.invite.code}/memberships`
				)
				setMembership(data.data)
				this.close()
				this.$router.push({
					name: 'chat',
					params: { chat: 'chat', id: data.data.chat_id },
				})
			} catch (e) {
				console.error(e)
				this.isRedeeming = false
				this.modalError = this.$refs.modal.$alert.error()
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

:deep(.modal-body) {
	padding: 24px;
	text-align: center;
	@include flex-column-center;
	justify-content: center;
}

.error-container {
	@include flex-column-center;
	.pronto-icon {
		color: var(--red-200);
		font-size: 63px;
		&:before {
			width: 1.5em;
			height: 1.5em;
		}
	}
}

.content {
	margin-top: 16px;
	> strong {
		@include fontFamily(800);
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.invite-preview-modal {
	.modal-body {
		padding: 24px;
		text-align: center;
		@include flex-column-center;
		justify-content: center;
	}
}
</style>
